
import { computed, ref } from "vue";

export default {
    name: "UploadDocuments",
    props: ['baseUrl'],
    setup(props: any) {
        const loggedIn = ref(false);
        const documentsList = ref<string[]>([]);
        const iconUrls = ref<{ [key: string]: string }>({});
        const OfficerId = localStorage.OfficerId;
        
        const uploadStatusMessage = ref('');
        const uploadStatusClass = ref('');
        
        // Store file input references
        const fileInputs = ref<{ [key: string]: HTMLInputElement | null }>({});
        
        // IMPORTANT: Guards use "Guardtoken" (different from admin's "token")
        var authToken = localStorage.getItem("Guardtoken")!;
        const baseUrlAddress = computed(() => `${props.baseUrl}`);

        // ============================================================
        // DOCUMENTS CONFIGURATION - Single source of truth for all rows
        // No face extraction for guards - upload only
        // ============================================================
        const documentsConfig = [
            { key: 'Passport', displayName: 'Passport', isPdf: false },
            { key: 'Photo', displayName: '35x45mm Photo', isPdf: false },
            { key: 'Visa', displayName: 'Visa Share Code', isPdf: false },
            { key: 'SIABadge', displayName: 'SIA Licence', isPdf: false },
            { key: 'NINoDoc', displayName: 'NIN/UTR', isPdf: false },
            { key: 'DrivingLicence', displayName: 'DrivingLicence', isPdf: false },
            { key: 'AddressDoc', displayName: 'Proof of Address 01', isPdf: false },
            { key: 'AdditionalDoc1', displayName: 'Proof of Address 02', isPdf: false },
            { key: 'AdditionalDoc2', displayName: 'Supporting Document 01', isPdf: false },
            { key: 'AdditionalDoc3', displayName: 'Supporting Document 02', isPdf: false },
            { key: 'documents', displayName: 'All Documents PDF', isPdf: true },
        ];

        const getIconUrl = (documentName: string) => {
            return iconUrls.value[documentName] || null;
        };
        
        const setFileInputRef = (docName: string, el: any) => {
            if (el) {
                fileInputs.value[docName] = el as HTMLInputElement;
            }
        };
        
        const triggerFileInput = (docName: string) => {
            if (fileInputs.value[docName]) {
                fileInputs.value[docName]?.click();
            }
        };
        
        // ============================================================
        // Upload ONLY - no face extraction for guards
        // ============================================================
        const handleFileChange = async (documentName: string, event: Event) => {
            const target = event.target as HTMLInputElement;
            if (!target.files || !target.files[0]) return;
            
            const file = target.files[0];
            
            const formData = new FormData();
            formData.append('profile_pic', file);
            formData.append('documentName', documentName + '.jpg');
            
            try {
                uploadStatusMessage.value = `📤 Uploading ${documentName}...`;
                uploadStatusClass.value = 'text-info';
                
                const response = await fetch(`${props.baseUrl}SecurityOfficers/${OfficerId}/upload`, {
                    method: 'POST',
                    headers: {
                        "Authorization": "Bearer " + localStorage.getItem("Guardtoken"),
                    },
                    body: formData
                });
                
                await response.json();
                
                // Refresh document list and icons
                await checkDocumentsStatus();
                await loadIcons();
                
                // Reset file input
                if (fileInputs.value[documentName]) {
                    fileInputs.value[documentName]!.value = '';
                }
                
                uploadStatusMessage.value = `✅ ${documentName} uploaded successfully!`;
                uploadStatusClass.value = 'text-success';
                
            } catch (error) {
                console.error("Upload error:", error);
                alert("Upload failed: " + (error as Error).message);
                uploadStatusMessage.value = `❌ Upload failed for ${documentName}`;
                uploadStatusClass.value = 'text-danger';
            } finally {
                setTimeout(() => { uploadStatusMessage.value = ''; }, 3000);
            }
        };
        
        const handlePDFFileChange = async (documentName: string, event: Event) => {
            const target = event.target as HTMLInputElement;
            if (!target.files || !target.files[0]) return;
            
            const file = target.files[0];
            
            const formData = new FormData();
            formData.append('profile_pic', file);
            formData.append('documentName', documentName + '.pdf');
            formData.append('isPdf', 'true');
            
            try {
                const response = await fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/upload', {
                    method: 'POST',
                    headers: {
                        "Authorization": "Bearer " + localStorage.getItem("Guardtoken"),
                    },
                    body: formData
                });
                
                await response.json();
                await checkDocumentsStatus();
                await loadIcons();
                
                // Reset file input
                if (fileInputs.value[documentName]) {
                    fileInputs.value[documentName]!.value = '';
                }
                
                uploadStatusMessage.value = `✅ ${documentName} uploaded successfully!`;
                uploadStatusClass.value = 'text-success';
                setTimeout(() => { uploadStatusMessage.value = ''; }, 3000);
                
            } catch (error) {
                console.error("Upload error:", error);
                alert("Upload failed: " + (error as Error).message);
            }
        };
        
        const loadIcons = async () => {
            if (!OfficerId) {
                console.error("No OfficerId found");
                return;
            }
            
            try {
                const response = await fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/icons', {
                    method: 'GET',
                    headers: {
                        "Authorization": "Bearer " + localStorage.getItem("Guardtoken"),
                    },
                });
                
                const data = await response.json();
                const urlMap: { [key: string]: string } = {};
                
                if (Array.isArray(data)) {
                    data.forEach((url: string) => {
                        const match = url.match(/\/([^/?]+)\.(jpg|jpeg|png|gif|ico)/i);
                        if (match) {
                            const fileName = match[1];
                            urlMap[fileName] = url;
                        }
                    });
                }
                
                iconUrls.value = urlMap;
            } catch (err) {
                console.error(err);
            }
        };
        
        const checkDocumentsStatus = async () => {
            if (!OfficerId) return;
            
            try {
                const response = await fetch(props.baseUrl + 'SecurityOfficers/' + OfficerId + '/documents', {
                    method: 'GET',
                    headers: {
                        "Authorization": "Bearer " + localStorage.getItem("Guardtoken"),
                    },
                });
                const data = await response.json();
                documentsList.value = Array.isArray(data) ? data : [];
            } catch (error) {
                console.error(error);
            }
        };
        
        const downloadDocument = (fileName: string) => {
            let extension = '';
            let downloadUrl = '';
            
            if (fileName === 'documents') {
                extension = '.pdf';
                downloadUrl = props.baseUrl + 'SecurityOfficers/' + OfficerId + '/download/' + fileName + extension;
            } else {
                extension = '.jpg';
                downloadUrl = props.baseUrl + 'SecurityOfficers/' + OfficerId + '/download/' + fileName + extension;
            }
            
            fetch(downloadUrl, {
                method: 'GET',
                headers: {
                    "Authorization": "Bearer " + localStorage.getItem("Guardtoken"),
                },
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Download failed: ${response.status}`);
                }
                return response.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName + extension;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(error => {
                console.error("Download error:", error);
                alert("Failed to download file. Please try again.");
            });
        };
        
        // Check authentication using Guardtoken
        const token = localStorage.getItem("Guardtoken");
        if (token == null) {
            loggedIn.value = false;
        } else {
            loggedIn.value = true;
            //checkDocumentsStatus();
            loadIcons();
        }
        
        return {
            loggedIn,
            documentsList,
            documentsConfig,
            getIconUrl,
            downloadDocument,
            uploadStatusMessage,
            uploadStatusClass,
            setFileInputRef,
            triggerFileInput,
            handleFileChange,
            handlePDFFileChange,
        };
    }
};
